import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { sha256File } from '../helpers/file-hash';
import { AppHttpService } from './app-http.service';

export interface ProjectMapSourceListChange {
  /** Source to select after the list reloads. */
  sourceId?: string;
  /** True when the caller wants the Pending tab shown with `sourceId` selected. */
  focus?: boolean;
}

export type ProjectMapImportStatus = 'uploaded' | 'inspecting' | 'processing' | 'needs_input'
  | 'ready_for_review' | 'published' | 'withdrawn' | 'failed' | 'cancelled';
export type ProjectMapSourceUnits = 'us_survey_foot' | 'foot' | 'metre' | 'degree';
export type ProjectMapSourceFormat = 'dxf' | 'geojson' | 'kml' | 'kmz' | 'pdf';
export const PROJECT_MAP_SOURCE_MAX_MB = 100;
export const PROJECT_MAP_SOURCE_MAX_BYTES = PROJECT_MAP_SOURCE_MAX_MB * 1024 * 1024;

export interface ProjectMapLegacyDisplay {
  sortOrder: number;
  defaultVisible: boolean;
  defaultOpacity: number;
  styleFallback?: string;
}

/**
 * One normalized artifact the converter produced. `raster_preview` is the
 * downsampled PNG of a `raster` sublayer's GeoTIFF, shown in the location
 * preview; versions imported before previews existed carry only the GeoTIFF.
 */
export interface ProjectMapSourceAsset {
  id: string;
  kind: 'geojson' | 'raster' | 'raster_preview';
  role: 'geometry' | 'preview';
  sublayerKey: string;
  bytes: number;
  sha256: string;
}

export interface ProjectMapSourceVersion {
  id: string;
  sourceId?: string;
  sourceKind?: string;
  sourceUrl?: string | null;
  sourceLayerIds?: string[];
  arcgisSelection?: { layerIds: number[]; extent: [number, number, number, number] | null } | null;
  legacyReferenceLayerId?: string;
  legacyDisplay?: ProjectMapLegacyDisplay;
  capture?: { captureInterval?: { startedAt: string; finishedAt: string }; warnings?: string[]; offlineEligible?: boolean };
  status: ProjectMapImportStatus;
  uploaded: boolean;
  filename: string;
  // `placed_plan`: a drawing revision placed from the Drawings tab
  format?: ProjectMapSourceFormat | 'placed_plan' | null;
  sourceCrs?: string | null;
  sourceUnits?: ProjectMapSourceUnits | null;
  requiredInputs: string[];
  warnings: string[];
  // `sourceColors` holds the distinct colours the imported file authored for
  // this sublayer, lowercase `#rrggbb`, in first-appearance order and capped
  // by the converter. ArcGIS captures never carry them. `fieldNames` lists the
  // readable (scalar, non-styling) attribute names, in first-appearance order.
  sublayers: { key: string; name: string; featureCount: number; geometryTypes: string[];
    bounds?: [number, number, number, number]; sourceColors?: string[]; sourceColorCount?: number;
    fieldNames?: string[] }[];
  assets: ProjectMapSourceAsset[];
  job?: { id: string; status: string; attempts?: number; failureCode?: string | null } | null;
}

export interface ProjectMapSourceSummary {
  id: string;
  name: string;
  /** `placed_plan`: a drawing revision placed from the Drawings tab */
  kind: ProjectMapSourceFormat | 'arcgis' | 'placed_plan';
  latestVersion: { id: string; versionNumber: number; status: ProjectMapImportStatus } | null;
}

export interface ProjectMapUploadGrant {
  url: string;
  method: 'PUT';
  headers: Record<string, string>;
  expiresIn: number;
}

export interface ProjectMapUploadIntent {
  source: { id: string; name: string; kind: ProjectMapSourceFormat };
  version: ProjectMapSourceVersion;
  upload: ProjectMapUploadGrant | null;
}

export interface ProjectMapUploadMetadata {
  name: string;
  filename: string;
  bytes: number;
  sha256: string;
}

export interface ProjectMapArcGISInspection {
  url: string;
  serviceType: 'FeatureServer' | 'MapServer';
  offlineEligible: boolean;
  rendererWarnings: string[];
  sublayers: { id: number; name: string; geometryType: string | null; objectIdField: string | null;
    supportsQuery: boolean; rendererWarnings: string[] }[];
}

@Injectable({ providedIn: 'root' })
export class ProjectMapSourceService {
  /**
   * Fired when the project's sources changed. A `focus` payload names a source
   * the list should bring into view (a freshly staged upload), so the Pending
   * tab opens on it instead of leaving the reader on an empty On the map tab.
   */
  readonly listChanged = new Subject<ProjectMapSourceListChange | void>();

  constructor(private http: AppHttpService) {}

  list(projectId: string, query = { offset: 0, limit: 50, search: '' }): Observable<{
    sources: ProjectMapSourceSummary[]; nextOffset: number | null;
  }> {
    return this.http.getService(`/v1/project/${projectId}/map-sources`, query);
  }

  stage(projectId: string, metadata: ProjectMapUploadMetadata, idempotencyKey: string, sourceId?: string): Observable<ProjectMapUploadIntent> {
    const suffix = sourceId ? `/${encodeURIComponent(sourceId)}/versions` : '';
    return this.http.postService(`/v1/project/${projectId}/map-sources${suffix}`, metadata, {}, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }).pipe(tap(() => this.listChanged.next()));
  }

  inspectArcGIS(projectId: string, url: string): Observable<ProjectMapArcGISInspection> {
    return this.http.postService(`/v1/project/${projectId}/map-arcgis-sources/inspect`, { url });
  }

  captureArcGIS(projectId: string, source: { name: string; url: string; layerIds: number[]; extent?: [number, number, number, number] },
    idempotencyKey: string, sourceId?: string): Observable<{ version: ProjectMapSourceVersion }> {
    const suffix = sourceId ? `/${encodeURIComponent(sourceId)}/versions` : '';
    return this.http.postService(`/v1/project/${projectId}/map-arcgis-sources${suffix}`, source, {}, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }).pipe(tap(() => this.listChanged.next()));
  }

  resumeUpload(projectId: string, versionId: string, metadata: {
    bytes: number; sha256: string;
  }): Observable<ProjectMapUploadIntent> {
    return this.http.postService(`/v1/project/${projectId}/map-source-versions/${versionId}/upload-intent`, metadata)
      .pipe(tap(() => this.listChanged.next()));
  }

  getVersion(projectId: string, versionId: string): Observable<ProjectMapSourceVersion> {
    return this.http.getService(`/v1/project/${projectId}/map-source-versions/${versionId}`);
  }

  process(projectId: string, versionId: string, location: {
    sourceCrs?: string; sourceUnits?: ProjectMapSourceUnits;
  } = {}): Observable<{ version: ProjectMapSourceVersion }> {
    return this.http.postService(`/v1/project/${projectId}/map-source-versions/${versionId}/process`, location)
      .pipe(tap(() => this.listChanged.next()));
  }

  /** Remove a source that never reached the map. The API refuses published or still-processing sources. */
  remove(projectId: string, sourceId: string): Observable<{ id: string; deleted: true }> {
    return this.http.deleteService(`/v1/project/${projectId}/map-sources/${sourceId}`)
      .pipe(tap(() => this.listChanged.next()));
  }

  cancel(projectId: string, versionId: string): Observable<ProjectMapSourceVersion> {
    return this.http.postService(`/v1/project/${projectId}/map-source-versions/${versionId}/cancel`, {})
      .pipe(tap(() => this.listChanged.next()));
  }

  async hashFile(file: Blob, signal?: AbortSignal): Promise<string> {
    if (!file.size || file.size > PROJECT_MAP_SOURCE_MAX_BYTES) {
      throw new Error(`Choose a file between 1 byte and ${PROJECT_MAP_SOURCE_MAX_MB} MB.`);
    }
    return sha256File(file, { signal });
  }

  async uploadFile(file: Blob, grant: ProjectMapUploadGrant, signal?: AbortSignal): Promise<void> {
    const url = new URL(grant.url);
    if (url.protocol !== 'https:' || url.username || url.password || grant.method !== 'PUT') {
      throw new Error('The file upload grant is invalid. Please try again.');
    }
    const headers = new Headers();
    for (const [name, value] of Object.entries(grant.headers)) {
      // The browser supplies the Blob's length; app authentication must never reach S3.
      if (!['content-length', 'token', 'authorization', 'cookie'].includes(name.toLowerCase())) {
        headers.set(name, value);
      }
    }
    const response = await fetch(url.toString(), {
      method: 'PUT', body: file, headers, signal, credentials: 'omit',
      redirect: 'error', referrerPolicy: 'no-referrer', cache: 'no-store',
    });
    // A retried immutable PUT may already exist. The process API verifies its actual hash/size.
    if (!response.ok && response.status !== 412) {
      throw new Error('The file could not be uploaded. Check your connection and try again.');
    }
  }

  statusLabel(status?: ProjectMapImportStatus): string {
    const labels: Record<ProjectMapImportStatus, string> = {
      uploaded: 'Awaiting processing', inspecting: 'Checking source', processing: 'Processing',
      needs_input: 'Location needs confirmation', ready_for_review: 'Ready for review',
      published: 'On the map', withdrawn: 'Withdrawn', failed: 'Could not process', cancelled: 'Cancelled',
    };
    return status ? labels[status] || 'Unavailable' : 'Unavailable';
  }

  /**
   * Render notes describe how a raster page was rasterized ("Rendered at 139 dpi
   * to stay within the 16 megapixel limit."). Every full-size plan sheet carries
   * one, and there is no action to take, so they are shown in review as
   * information rather than counted as warnings.
   */
  isRenderNote(message: string): boolean {
    return message.startsWith('Rendered at ');
  }

  failureMessage(code?: string | null, format: ProjectMapSourceFormat | null = 'dxf'): string {
    const unreadable: Partial<Record<ProjectMapSourceFormat, string>> = {
      dxf: 'This drawing could not be read. Export a supported ASCII DXF and import it again.',
      pdf: 'This PDF could not be read. Check that it opens normally and is not password protected.',
    };
    const messages: Record<string, string> = {
      COORDINATE_TRANSFORM_UNAVAILABLE: 'Confirm the source coordinate system. A grid required to locate this data is unavailable.',
      CONVERTER_VERSION_MISMATCH: 'Processing is unavailable for this source version. Contact your project administrator before importing it again.',
      SOURCE_CONVERSION_FAILED: unreadable[format] || 'This file could not be read. Check that it is a valid source file and import it again.',
      PDF_NOT_GEOREFERENCED: 'This PDF has no embedded georeferencing. Export a georeferenced PDF from your CAD or GIS tool, or upload the sheet as a KMZ.',
      CONVERTER_TIMEOUT: 'This data took too long to process. Export a smaller area or simplify it, then import it again.',
      IMPORT_FAILED: 'Processing was interrupted. Upload the file again or contact your project administrator.',
    };
    return messages[code || ''] || 'The map data could not be processed. Review the source and try again.';
  }
}
